/**
 * Minimalistická desetinná aritmetika nad BigInt — zrcadlí podmnožinu
 * java.math.BigDecimal, kterou používá výpočetní jádro `funds/calc`
 * (portilio-be, větev feature/modul-distribuce). Nikdy nepočítá ve float:
 * `number` vstupy se převádí přes svůj přesný desítkový zápis (String).
 *
 * Pozn.: záměrně bez BigInt literálů (`0n`) — tsconfig target ES2019 a build
 * portilio-fe (fáze 8) by je nemusel umět parsovat; `BigInt(n)` je bezpečné.
 */

const B0 = BigInt(0)
const B1 = BigInt(1)
const B2 = BigInt(2)
const B10 = BigInt(10)

function pow10(exponent: number): bigint {
  // Iterativně (ne operátor **) — tsconfig portilio-fe má target es5, kde ** na bigint neprojde.
  let result = B1
  for (let i = 0; i < exponent; i++) {
    result = result * B10
  }
  return result
}

/** Celočíselné dělení kladných čísel se zaokrouhlením HALF_UP. */
function divRoundHalfUpPositive(numerator: bigint, divisor: bigint): bigint {
  const quotient = numerator / divisor
  const remainder = numerator % divisor
  return remainder * B2 >= divisor ? quotient + B1 : quotient
}

export type DecInput = Dec | number | string

export class Dec {
  private constructor(
    /** Hodnota bez desetinné čárky (vč. znaménka) — jako BigDecimal unscaledValue. */
    readonly unscaled: bigint,
    /** Počet desetinných míst (vždy >= 0). */
    readonly scale: number
  ) {}

  static readonly ZERO = new Dec(B0, 0)
  static readonly ONE = new Dec(B1, 0)

  static of(value: DecInput): Dec {
    if (value instanceof Dec) {
      return value
    }
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) {
        throw new Error(`Nevalidní číslo: ${value}`)
      }
      return Dec.parse(String(value))
    }
    return Dec.parse(value)
  }

  /** Parsuje desítkový zápis vč. exponentu (String(number) umí vrátit „1e-7"). */
  static parse(text: string): Dec {
    const match = /^([+-]?)(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/.exec(text.trim())
    if (!match) {
      throw new Error(`Nevalidní číslo: ${text}`)
    }
    const [, signPart, intPart, fracPart = '', expPart] = match
    let unscaled = BigInt(intPart + fracPart)
    if (signPart === '-') {
      unscaled = -unscaled
    }
    // Exponent posouvá desetinnou čárku; záporný výsledný scale doženeme násobením.
    let scale = fracPart.length - (expPart ? Number(expPart) : 0)
    if (scale < 0) {
      unscaled *= pow10(-scale)
      scale = 0
    }
    return new Dec(unscaled, scale)
  }

  /** Obě hodnoty na společném (větším) scale — pro sčítání a porovnání. */
  private static aligned(a: Dec, b: Dec): [bigint, bigint, number] {
    const scale = Math.max(a.scale, b.scale)
    return [a.unscaled * pow10(scale - a.scale), b.unscaled * pow10(scale - b.scale), scale]
  }

  add(other: DecInput): Dec {
    const [a, b, scale] = Dec.aligned(this, Dec.of(other))
    return new Dec(a + b, scale)
  }

  sub(other: DecInput): Dec {
    const [a, b, scale] = Dec.aligned(this, Dec.of(other))
    return new Dec(a - b, scale)
  }

  /** Přesné násobení (scale se sčítají) — jako BigDecimal.multiply. */
  mul(other: DecInput): Dec {
    const o = Dec.of(other)
    return new Dec(this.unscaled * o.unscaled, this.scale + o.scale)
  }

  /** Změna počtu desetinných míst; při snižování zaokrouhluje HALF_UP. */
  setScale(scale: number): Dec {
    if (scale === this.scale) {
      return this
    }
    if (scale > this.scale) {
      return new Dec(this.unscaled * pow10(scale - this.scale), scale)
    }
    const divisor = pow10(this.scale - scale)
    const negative = this.unscaled < B0
    const abs = negative ? -this.unscaled : this.unscaled
    const rounded = divRoundHalfUpPositive(abs, divisor)
    return new Dec(negative ? -rounded : rounded, scale)
  }

  /** Dělení se zaokrouhlením HALF_UP na daný scale — jako BigDecimal.divide(b, scale, HALF_UP). */
  div(other: DecInput, scale: number): Dec {
    const o = Dec.of(other)
    if (o.unscaled === B0) {
      throw new Error('Dělení nulou')
    }
    // this/other * 10^scale = this.unscaled * 10^(other.scale + scale) / (other.unscaled * 10^this.scale)
    const numerator = this.unscaled * pow10(o.scale + scale)
    const denominator = o.unscaled * pow10(this.scale)
    const negative = numerator < B0 !== denominator < B0
    const absNumerator = numerator < B0 ? -numerator : numerator
    const absDenominator = denominator < B0 ? -denominator : denominator
    const rounded = divRoundHalfUpPositive(absNumerator, absDenominator)
    return new Dec(negative && rounded !== B0 ? -rounded : rounded, scale)
  }

  cmp(other: DecInput): number {
    const [a, b] = Dec.aligned(this, Dec.of(other))
    return a < b ? -1 : a > b ? 1 : 0
  }

  signum(): number {
    return this.unscaled < B0 ? -1 : this.unscaled > B0 ? 1 : 0
  }

  abs(): Dec {
    return this.unscaled < B0 ? new Dec(-this.unscaled, this.scale) : this
  }

  max(other: DecInput): Dec {
    const o = Dec.of(other)
    return this.cmp(o) >= 0 ? this : o
  }

  /** Přesný desítkový zápis vč. koncových nul dle scale (BigDecimal.toPlainString). */
  toString(): string {
    const negative = this.unscaled < B0
    const digits = (negative ? -this.unscaled : this.unscaled).toString()
    const sign = negative ? '-' : ''
    if (this.scale === 0) {
      return sign + digits
    }
    const padded = digits.padStart(this.scale + 1, '0')
    const splitAt = padded.length - this.scale
    return `${sign}${padded.slice(0, splitAt)}.${padded.slice(splitAt)}`
  }

  /** Pro UI/mock — nad ~15 platných číslic ztrácí přesnost (BE zůstává BigDecimal). */
  toNumber(): number {
    return Number(this.toString())
  }
}
